import React from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as R from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { useControlledForm, useFormApiError, useFormReset } from 'hooks'
import { users } from 'orm'
import { mapPayloadToInternalState } from './actions'
import { getSchema } from './schema'
import { getUsers } from 'ducks/users'

export const NAME_FIELD = 'name'
export const MEMBERS_FIELD = 'users'

export const defaultValues = {
  [NAME_FIELD]: '',
  [MEMBERS_FIELD]: [],
}

const useDepartmentManagerForm = (state, handlers) => {
  const { department, isOpen } = state
  const { onConfirm } = handlers

  const [apiError, setApiError] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)
  const members = useSelector(getUsers)

  const schema = getSchema()
  const dispatch = useDispatch()

  const internalState = React.useMemo(
    () => mapPayloadToInternalState(department),
    [department],
  )

  const handleFormData = React.useCallback(
    async data => {
      await onConfirm(data, {
        onError: setApiError,
        onSuccess: () => setShouldReset(true),
      })
    },
    [onConfirm],
  )

  React.useEffect(() => {
    dispatch(users.fetchStart())
  }, [dispatch])

  const {
    control,
    formState,
    handleFormApiError,
    handleFormReset,
    handleFormSubmit,
    watch,
  } = useControlledForm({
    values: department ? internalState : defaultValues,
    resolver: yupResolver(schema),
    handleFormData,
  })

  useFormApiError({ apiError }, handleFormApiError)
  useFormReset(
    { shouldReset: R.and(shouldReset, formState.isSubmitSuccessful) },
    handleFormReset,
  )

  React.useEffect(() => {
    if (isOpen) {
      setShouldReset(false)
    }
  }, [isOpen])

  return {
    state: {
      control,
      formState,
      members,
    },
    handlers: {
      handleFormReset,
      handleFormSubmit,
      watch,
    },
  }
}

export const useModal = (state, handlers) => {
  const { department } = state
  const { onCancel, onClose, onConfirm } = handlers

  const {
    state: { control, formState, members },
    handlers: { handleFormReset, handleFormSubmit, watch },
  } = useDepartmentManagerForm({ department }, { onConfirm })

  const handleResetState = React.useCallback(() => {
    handleFormReset()
  }, [handleFormReset])

  const handleCancel = React.useCallback(() => {
    handleResetState()
    onCancel()
  }, [handleResetState, onCancel])

  const handleClose = React.useCallback(() => {
    handleResetState()
    onClose()
  }, [handleResetState, onClose])

  return {
    handlers: {
      handleCancel,
      handleClose,
      watch,
      handleFormSubmit,
    },
    state: {
      control,
      formState,
      members,
    },
  }
}
